import React, { useState, useMemo } from 'react';
import { Chess } from 'chess.js';
import toast from 'react-hot-toast';
import { PIECE_IMAGES, BOTS } from '../../utils/AppConstants';

const MiniPreviewBoard = React.memo(({ fen }) => {
    const temp = new Chess(); 
    try { 
        temp.load(fen); 
    } catch(e) {
        try { temp.load('rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1'); } catch(err){}
    }
    const rows = [8,7,6,5,4,3,2,1]; 
    const cols = ['a','b','c','d','e','f','g','h'];
    const render = {}; 
    temp.board().forEach((row, r) => row.forEach((sq, c) => { 
        if (sq) render[`${cols[c]}${rows[r]}`] = sq.color + sq.type.toUpperCase(); 
    }));
    return (
        <div style={{ 
            width: '220px', 
            height: '220px', 
            display: 'grid', 
            gridTemplateColumns: 'repeat(8, 1fr)', 
            gridTemplateRows: 'repeat(8, 1fr)', 
            border: '3px solid rgba(255, 255, 255, 0.75)', 
            borderRadius: '10px', 
            overflow: 'hidden', 
            boxShadow: 'inset 3px 3px 6px rgba(163, 177, 198, 0.35), inset -3px -3px 6px rgba(255, 255, 255, 0.75), 2px 2px 8px rgba(163, 177, 198, 0.15)', 
            margin: '10px 0' 
        }}>
            {rows.map((row, rIdx) => cols.map((col, cIdx) => {
                const id = `${col}${row}`; 
                const isDark = (rIdx + cIdx) % 2 === 1;
                return ( 
                    <div key={id} style={{ background: isDark ? '#9ca3af' : '#e5e7eb', display: 'flex', justifyContent: 'center', alignItems: 'center', position: 'relative' }}> 
                        {render[id] && <img src={PIECE_IMAGES[render[id]]} alt="" style={{ width: '90%', height: '90%', filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.15))' }} />} 
                    </div> 
                );
            }))}
        </div>
    );
});

export default function CoachHistory({ classesList, usersMap }) {
    const [activeTab, setActiveTab] = useState('classes'); 
    const [expandedClass, setExpandedClass] = useState(null); 
    const [expandedMatches, setExpandedMatches] = useState({});
    const [expandedTimelines, setExpandedTimelines] = useState({});
    const [searchQuery, setSearchQuery] = useState('');
    const [timeframeFilter, setTimeframeFilter] = useState('all');
    const [visibleBoards, setVisibleBoards] = useState({});

    const toggleBoard = (key, e) => {
        if (e) e.stopPropagation();
        setVisibleBoards(prev => ({ ...prev, [key]: !prev[key] }));
    };

    const toggleMatch = (matchId) => {
        setExpandedMatches(prev => ({ ...prev, [matchId]: !prev[matchId] }));
    };

    const toggleTimeline = (classId, e) => {
        if (e) e.stopPropagation();
        setExpandedTimelines(prev => ({ ...prev, [classId]: !prev[classId] }));
    };

    const completedClasses = useMemo(() => {
        return classesList
            .filter(c => c.status === 'COMPLETED')
            .sort((a, b) => (b.endedAt || 0) - (a.endedAt || 0));
    }, [classesList]);

    const myMatchHistory = useMemo(() => {
        const list = [];
        classesList.forEach(cls => {
            if (cls.activeGames) { 
                Object.entries(cls.activeGames).forEach(([gId, game]) => { 
                    list.push({ ...game, id: gId, className: cls.topic, date: game.lastMoveAt || cls.createdAt || Date.now() }); 
                }); 
            }
        });
        return list.sort((a, b) => b.date - a.date);
    }, [classesList]);

    // --- QUICK STATISTICS COMPUTATIONS ---
    const stats = useMemo(() => {
        let totalMinutes = 0;
        completedClasses.forEach(cls => {
            if (cls.endedAt && cls.createdAt) {
                const diffMin = Math.round((cls.endedAt - cls.createdAt) / 60000);
                if (diffMin > 5 && diffMin < 240) {
                    totalMinutes += diffMin;
                } else {
                    totalMinutes += 60;
                }
            } else {
                totalMinutes += 60;
            }
        });
        const totalHours = (totalMinutes / 60).toFixed(1);

        let sumRates = 0;
        let countWithStudents = 0;
        completedClasses.forEach(cls => {
            const presenceMap = cls.presence || {};
            const presentCount = Object.values(presenceMap).filter(v => v === true).length;
            const totalCount = cls.students?.length || 0;
            if (totalCount > 0) {
                sumRates += (presentCount / totalCount);
                countWithStudents++;
            }
        });
        const avgAttendance = countWithStudents > 0 ? Math.round((sumRates / countWithStudents) * 100) : 100;

        return {
            classesCount: completedClasses.length,
            hours: totalHours,
            attendance: avgAttendance,
            matchesCount: myMatchHistory.length
        };
    }, [completedClasses, myMatchHistory]);

    // --- FILTERS ---
    const filteredClasses = useMemo(() => {
        return completedClasses.filter(cls => {
            if (timeframeFilter === 'week') {
                const oneWeekAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
                if ((cls.endedAt || cls.createdAt) < oneWeekAgo) return false;
            } else if (timeframeFilter === 'month') {
                const oneMonthAgo = Date.now() - 30 * 24 * 60 * 60 * 1000;
                if ((cls.endedAt || cls.createdAt) < oneMonthAgo) return false;
            }

            if (!searchQuery) return true;
            const q = searchQuery.toLowerCase();
            const topicMatch = cls.topic?.toLowerCase().includes(q);
            const notesMatch = cls.coachStickyNote?.toLowerCase().includes(q);
            const privateNotesMatch = cls.coachClassPrivateNotes?.toLowerCase().includes(q);
            const studentMatch = cls.students?.some(email => {
                const name = usersMap[email] || email;
                return name.toLowerCase().includes(q);
            });

            return topicMatch || notesMatch || privateNotesMatch || studentMatch;
        });
    }, [completedClasses, searchQuery, timeframeFilter, usersMap]);

    const filteredMatches = useMemo(() => {
        return myMatchHistory.filter(g => {
            if (timeframeFilter === 'week') {
                const oneWeekAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
                if (g.date < oneWeekAgo) return false;
            } else if (timeframeFilter === 'month') {
                const oneMonthAgo = Date.now() - 30 * 24 * 60 * 60 * 1000;
                if (g.date < oneMonthAgo) return false;
            }

            if (!searchQuery) return true;
            const q = searchQuery.toLowerCase();
            const whiteName = g.white.startsWith('bot_') ? (BOTS.find(b=>b.id===g.white)?.name || 'Bot') : (usersMap[g.white] || g.white);
            const blackName = g.black.startsWith('bot_') ? (BOTS.find(b=>b.id===g.black)?.name || 'Bot') : (usersMap[g.black] || g.black);
            const classMatch = g.className?.toLowerCase().includes(q);

            return whiteName.toLowerCase().includes(q) || blackName.toLowerCase().includes(q) || classMatch;
        });
    }, [myMatchHistory, searchQuery, timeframeFilter, usersMap]);

    // --- TIMELINE BADGE FORMATTER ---
    const getTimelineBadge = (title) => {
        const t = title.toLowerCase();
        if (t.includes('started class') || t.includes('class started')) return { icon: '🎓', color: '#3b82f6', bg: 'rgba(59, 130, 246, 0.15)' };
        if (t.includes('ended class') || t.includes('class ended')) return { icon: '🏁', color: '#ef4444', bg: 'rgba(239, 68, 68, 0.15)' };
        if (t.includes('puzzle') || t.includes('quiz') || t.includes('mcq') || t.includes('math')) return { icon: '🧩', color: '#f59e0b', bg: 'rgba(245, 158, 11, 0.15)' };
        if (t.includes('solo') || t.includes('play') || t.includes('game') || t.includes('arena')) return { icon: '🎮', color: '#a855f7', bg: 'rgba(168, 85, 247, 0.15)' };
        if (t.includes('whiteboard') || t.includes('draw') || t.includes('clear')) return { icon: '🧹', color: '#64748b', bg: 'rgba(100, 116, 139, 0.15)' };
        return { icon: '📝', color: '#10b981', bg: 'rgba(16, 185, 129, 0.15)' };
    };

    const renderAttendance = (cls) => {
        const total = cls.students || [];
        const presenceData = cls.presence || {};
        const joined = total.filter(email => presenceData[email.replace(/\./g, '_')] === true);
        const absent = total.filter(email => presenceData[email.replace(/\./g, '_')] !== true);
        const percent = total.length > 0 ? Math.round((joined.length / total.length) * 100) : 0;

        return (
            <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap', alignItems: 'center' }}>
                {/* SVG Progress Circle */}
                <div style={{ position: 'relative', width: '70px', height: '70px', display: 'flex', justifyContent: 'center', alignItems: 'center', flexShrink: 0 }}>
                    <svg width="70" height="70" viewBox="0 0 36 36">
                        <path stroke="rgba(163, 177, 198, 0.2)" strokeWidth="3.5" fill="none" d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" />
                        <path stroke={percent > 75 ? '#10b981' : percent > 40 ? '#f59e0b' : '#ef4444'} strokeWidth="3.5" strokeDasharray={`${percent}, 100`} strokeLinecap="round" fill="none" d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" />
                    </svg>
                    <div style={{ position: 'absolute', color: 'var(--text-primary)', fontWeight: 'bold', fontSize: '13px' }}>{percent}%</div>
                </div>

                <div style={{ flex: 1, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px' }}>
                    <div style={{ background: '#e0e5ec', padding: '12px 16px', borderRadius: '12px', border: '1px solid rgba(255, 255, 255, 0.45)', boxShadow: 'inset 2px 2px 5px rgba(163,177,198,0.25), inset -2px -2px 5px rgba(255,255,255,0.7)' }}>
                        <div style={{ color: '#10b981', fontWeight: 'bold', marginBottom: '8px', fontSize: '12px', letterSpacing: '0.5px' }}>✅ JOINED ({joined.length})</div>
                        <div style={{ display: 'flex', gap: '8px', flexDirection: 'column' }}>
                            {joined.length === 0 ? (
                                <span style={{ color: 'var(--text-secondary)', fontSize: '13px', fontStyle: 'italic' }}>None</span>
                            ) : (
                                joined.map(e => {
                                    const safeEmail = e.replace(/\./g, '_');
                                    const remark = cls.studentClassRemarks?.[safeEmail];
                                    return (
                                        <div key={e} style={{ display: 'flex', flexDirection: 'column', gap: '4px', background: 'var(--bg-primary)', border: '1px solid rgba(255,255,255,0.65)', boxShadow: '1.5px 1.5px 4px rgba(163,177,198,0.2), -1.5px -1.5px 4px rgba(255,255,255,0.85)', padding: '5px 10px', borderRadius: '10px' }}>
                                            <span style={{ color: 'var(--text-primary)', fontSize: '12.5px', fontWeight: 'bold' }}>👤 {usersMap[e] || e.split('@')[0]}</span>
                                            {remark && (
                                                <span style={{ color: '#be185d', fontSize: '11px', fontStyle: 'italic', fontWeight: '600', paddingLeft: '14px', position: 'relative' }}>
                                                    💬 "{remark}"
                                                </span>
                                            )}
                                        </div>
                                    );
                                })
                            )}
                        </div>
                    </div>
                    <div style={{ background: '#e0e5ec', padding: '12px 16px', borderRadius: '12px', border: '1px solid rgba(255, 255, 255, 0.45)', boxShadow: 'inset 2px 2px 5px rgba(163,177,198,0.25), inset -2px -2px 5px rgba(255,255,255,0.7)' }}>
                        <div style={{ color: '#ef4444', fontWeight: 'bold', marginBottom: '8px', fontSize: '12px', letterSpacing: '0.5px' }}>❌ ABSENT ({absent.length})</div>
                        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                            {absent.length === 0 ? (
                                <span style={{ color: 'var(--text-secondary)', fontSize: '13px', fontStyle: 'italic' }}>None</span>
                            ) : (
                                absent.map(e => (
                                    <span key={e} style={{ background: 'var(--bg-primary)', border: '1px solid rgba(255,255,255,0.6)', boxShadow: '1.5px 1.5px 4px rgba(163,177,198,0.2), -1.5px -1.5px 4px rgba(255,255,255,0.85)', padding: '4px 10px', borderRadius: '15px', fontSize: '12px', color: 'var(--text-secondary)' }}>
                                        👤 {usersMap[e] || e.split('@')[0]}
                                    </span>
                                ))
                            )}
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    const renderTimeline = (cls, classIdx) => {
        const logs = cls.activityLog || [];
        const isTimelineOpen = !!expandedTimelines[cls.id];
        
        return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <button 
                    onClick={(e) => toggleTimeline(cls.id, e)}
                    style={{ 
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        width: '100%',
                        padding: '10px 14px', 
                        background: '#e0e5ec', 
                        border: '1px solid rgba(255,255,255,0.65)', 
                        color: 'var(--text-primary)', 
                        borderRadius: '10px', 
                        fontWeight: 'bold', 
                        cursor: 'pointer', 
                        fontSize: '12px', 
                        boxShadow: isTimelineOpen ? 'var(--neu-shadow-inset)' : '2px 2px 6px rgba(163,177,198,0.25), -2px -2px 6px rgba(255,255,255,0.7)',
                        transition: 'all 0.2s'
                    }}
                >
                    <span>⏳ Click to view Class Activity Log ({logs.length} events)</span>
                    <span>{isTimelineOpen ? '▲ Hide' : '▼ Expand'}</span>
                </button>

                {isTimelineOpen && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '15px', position: 'relative', paddingLeft: '15px', marginTop: '10px', animation: 'fadeIn 0.25s ease-out' }}>
                        {logs.length === 0 ? (
                            <div style={{ color: 'var(--text-secondary)', fontSize: '13px', fontStyle: 'italic' }}>No timeline events recorded.</div>
                        ) : (
                            <>
                                <div style={{ position: 'absolute', top: '8px', bottom: '8px', left: '24px', width: '2px', background: 'rgba(163, 177, 198, 0.3)' }}></div>
                                {logs.map((log, idx) => {
                                    const badge = getTimelineBadge(log.title);
                                    const boardKey = `timeline-${classIdx}-${idx}`;
                                    return (
                                        <div key={idx} style={{ display: 'flex', gap: '15px', alignItems: 'flex-start', position: 'relative', zIndex: 1 }}>
                                            {/* Circle badge */}
                                            <div style={{ 
                                                width: '20px', 
                                                height: '20px', 
                                                borderRadius: '50%', 
                                                background: badge.bg, 
                                                border: `2px solid ${badge.color}`, 
                                                display: 'flex', 
                                                justifyContent: 'center', 
                                                alignItems: 'center',
                                                fontSize: '10px',
                                                color: badge.color,
                                                flexShrink: 0
                                            }}>
                                                {badge.icon}
                                            </div>
                                            
                                            <div style={{ flex: 1 }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                                                    <span style={{ color: 'var(--text-primary)', fontSize: '13.5px', fontWeight: '600' }}>{log.title}</span>
                                                    <span style={{ color: 'var(--text-secondary)', fontSize: '11px' }}>
                                                        {new Date(log.time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                                                    </span>
                                                </div>
                                                {log.fen && (
                                                    <div style={{ display: 'flex', gap: '10px', alignItems: 'center', marginTop: '6px' }}>
                                                        <button 
                                                            onClick={() => { navigator.clipboard.writeText(log.fen); toast.success('FEN Copied!'); }}
                                                            style={{ background: 'var(--bg-primary)', border: '1px solid rgba(255,255,255,0.6)', boxShadow: '1.5px 1.5px 4px rgba(163,177,198,0.2), -1.5px -1.5px 4px rgba(255,255,255,0.85)', borderRadius: '6px', color: '#10b981', fontSize: '11px', padding: '4px 10px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px', fontWeight: 'bold' }}
                                                        >
                                                            📋 Copy FEN
                                                        </button>
                                                        <button 
                                                            onClick={(e) => toggleBoard(boardKey, e)}
                                                            style={{ background: 'var(--bg-primary)', border: '1px solid rgba(255,255,255,0.6)', boxShadow: '1.5px 1.5px 4px rgba(163,177,198,0.2), -1.5px -1.5px 4px rgba(255,255,255,0.85)', borderRadius: '6px', color: '#3b82f6', fontSize: '11px', padding: '4px 10px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px', fontWeight: 'bold' }}
                                                        >
                                                            👁️ {visibleBoards[boardKey] ? 'Hide Board' : 'Preview Board'}
                                                        </button>
                                                    </div>
                                                )}
                                                {log.fen && visibleBoards[boardKey] && (
                                                    <div style={{ animation: 'fadeIn 0.2s ease-out' }}>
                                                        <MiniPreviewBoard fen={log.fen} />
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </>
                        )}
                    </div>
                )}
            </div>
        );
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '30px', width: '100%' }}>
            
            {/* Quick Metrics Section */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '25px' }}>
                <div style={{ background: 'var(--bg-primary)', border: '1px solid rgba(255,255,255,0.65)', padding: '20px', borderRadius: '16px', display: 'flex', alignItems: 'center', gap: '15px', boxShadow: 'var(--panel-shadow)' }}>
                    <div style={{ fontSize: '30px', background: 'rgba(236, 72, 153, 0.12)', width: '60px', height: '60px', borderRadius: '50%', display: 'flex', justifyContent: 'center', alignItems: 'center', boxShadow: 'inset 2px 2px 5px rgba(163,177,198,0.25), inset -2px -2px 5px rgba(255,255,255,0.75)' }}>📚</div>
                    <div>
                        <div style={{ fontSize: '24px', color: 'var(--text-primary)', fontWeight: 'bold' }}>{stats.classesCount}</div>
                        <div style={{ fontSize: '11px', color: '#ec4899', fontWeight: '750', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Completed Classes</div>
                    </div>
                </div>
                
                <div style={{ background: 'var(--bg-primary)', border: '1px solid rgba(255,255,255,0.65)', padding: '20px', borderRadius: '16px', display: 'flex', alignItems: 'center', gap: '15px', boxShadow: 'var(--panel-shadow)' }}>
                    <div style={{ fontSize: '30px', background: 'rgba(59, 130, 246, 0.12)', width: '60px', height: '60px', borderRadius: '50%', display: 'flex', justifyContent: 'center', alignItems: 'center', boxShadow: 'inset 2px 2px 5px rgba(163,177,198,0.25), inset -2px -2px 5px rgba(255,255,255,0.75)' }}>⏱️</div>
                    <div>
                        <div style={{ fontSize: '24px', color: 'var(--text-primary)', fontWeight: 'bold' }}>{stats.hours} hr</div>
                        <div style={{ fontSize: '11px', color: '#3b82f6', fontWeight: '750', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Total Classroom Time</div>
                    </div>
                </div>
                
                <div style={{ background: 'var(--bg-primary)', border: '1px solid rgba(255,255,255,0.65)', padding: '20px', borderRadius: '16px', display: 'flex', alignItems: 'center', gap: '15px', boxShadow: 'var(--panel-shadow)' }}>
                    <div style={{ fontSize: '30px', background: 'rgba(16, 185, 129, 0.12)', width: '60px', height: '60px', borderRadius: '50%', display: 'flex', justifyContent: 'center', alignItems: 'center', boxShadow: 'inset 2px 2px 5px rgba(163,177,198,0.25), inset -2px -2px 5px rgba(255,255,255,0.75)' }}>📈</div>
                    <div>
                        <div style={{ fontSize: '24px', color: 'var(--text-primary)', fontWeight: 'bold' }}>{stats.attendance}%</div>
                        <div style={{ fontSize: '11px', color: '#10b981', fontWeight: '750', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Avg Attendance Rate</div>
                    </div>
                </div>

                <div style={{ background: 'var(--bg-primary)', border: '1px solid rgba(255,255,255,0.65)', padding: '20px', borderRadius: '16px', display: 'flex', alignItems: 'center', gap: '15px', boxShadow: 'var(--panel-shadow)' }}>
                    <div style={{ fontSize: '30px', background: 'rgba(245, 158, 11, 0.12)', width: '60px', height: '60px', borderRadius: '50%', display: 'flex', justifyContent: 'center', alignItems: 'center', boxShadow: 'inset 2px 2px 5px rgba(163,177,198,0.25), inset -2px -2px 5px rgba(255,255,255,0.75)' }}>⚔️</div>
                    <div>
                        <div style={{ fontSize: '24px', color: 'var(--text-primary)', fontWeight: 'bold' }}>{stats.matchesCount}</div>
                        <div style={{ fontSize: '11px', color: '#f59e0b', fontWeight: '750', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Arena Games Logged</div>
                    </div>
                </div>
            </div>

            {/* Main Panel Box */}
            <div style={{ background: 'var(--bg-primary)', padding: '30px', borderRadius: '20px', boxShadow: 'var(--panel-shadow)', border: '1px solid rgba(255,255,255,0.55)' }}>
                
                {/* Tabs, Search, and Filters header */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px', flexWrap: 'wrap', gap: '20px', borderBottom: '1px solid rgba(163, 177, 198, 0.25)', paddingBottom: '20px' }}>
                    
                    {/* Tab Selectors */}
                    <div style={{ display: 'flex', background: '#e0e5ec', padding: '6px', borderRadius: '12px', boxShadow: 'var(--neu-shadow-inset)', border: '1px solid rgba(255,255,255,0.45)' }}>
                        <button 
                            onClick={() => setActiveTab('classes')} 
                            style={{ padding: '8px 18px', background: activeTab === 'classes' ? '#e0e5ec' : 'transparent', color: activeTab === 'classes' ? '#ec4899' : '#374151', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer', transition: 'all 0.2s', fontSize: '14px', boxShadow: activeTab === 'classes' ? '3px 3px 8px rgba(163,177,198,0.4), -3px -3px 8px rgba(255,255,255,0.8)' : 'none', border: activeTab === 'classes' ? '1px solid rgba(255,255,255,0.6)' : 'none' }}
                        >
                            📚 Past Classes ({filteredClasses.length})
                        </button>
                        <button 
                            onClick={() => setActiveTab('matches')} 
                            style={{ padding: '8px 18px', background: activeTab === 'matches' ? '#e0e5ec' : 'transparent', color: activeTab === 'matches' ? '#3b82f6' : '#374151', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer', transition: 'all 0.2s', fontSize: '14px', boxShadow: activeTab === 'matches' ? '3px 3px 8px rgba(163,177,198,0.4), -3px -3px 8px rgba(255,255,255,0.8)' : 'none', border: activeTab === 'matches' ? '1px solid rgba(255,255,255,0.6)' : 'none' }}
                        >
                            ⚔️ Arena Matches ({filteredMatches.length})
                        </button>
                    </div>

                    {/* Filter and Search box */}
                    <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'center' }}>
                        <select 
                            value={timeframeFilter} 
                            onChange={(e) => setTimeframeFilter(e.target.value)} 
                            style={{ padding: '10px 14px', background: '#e0e5ec', border: '1px solid rgba(255, 255, 255, 0.45)', borderRadius: '8px', color: 'var(--text-primary)', fontSize: '13px', outline: 'none', cursor: 'pointer', boxShadow: 'var(--neu-shadow-inset)', fontWeight: 'bold' }}
                        >
                            <option value="all">📅 All Time</option>
                            <option value="week">📅 Past 7 Days</option>
                            <option value="month">📅 Past 30 Days</option>
                        </select>

                        <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                            <input 
                                type="text" 
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                placeholder={activeTab === 'classes' ? 'Search topic or student...' : 'Search players...'}
                                style={{ padding: '10px 14px', paddingLeft: '32px', background: '#e0e5ec', border: '1px solid rgba(255, 255, 255, 0.45)', borderRadius: '8px', color: 'var(--text-primary)', fontSize: '13px', width: '220px', outline: 'none', boxShadow: 'var(--neu-shadow-inset)' }}
                            />
                            <span style={{ position: 'absolute', left: '10px', color: 'var(--text-secondary)', fontSize: '13px', pointerEvents: 'none' }}>🔍</span>
                        </div>
                    </div>
                </div>

                {/* Content Lists */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                    
                    {activeTab === 'classes' && (
                        filteredClasses.length === 0 ? (
                            <div style={{ color: 'var(--text-secondary)', textAlign: 'center', padding: '40px', background: '#e0e5ec', borderRadius: '12px', boxShadow: 'var(--neu-shadow-inset)', border: '1px solid rgba(255,255,255,0.45)' }}>
                                📭 No completed classes matches your criteria.
                            </div>
                        ) : (
                            filteredClasses.map((cls, i) => {
                                const isExpanded = expandedClass === cls.id;
                                const attendanceCount = Object.values(cls.presence || {}).filter(v => v === true).length;
                                return (
                                    <div key={cls.id || i} style={{ 
                                        background: '#e0e5ec', 
                                        borderRadius: '16px', 
                                        border: '1px solid rgba(255,255,255,0.55)', 
                                        boxShadow: isExpanded ? 'var(--panel-shadow-hover)' : 'var(--panel-shadow)',
                                        overflow: 'hidden', 
                                        transition: 'all 0.25s ease-in-out' 
                                    }}>
                                        {/* Header Row */}
                                        <div 
                                            onClick={() => setExpandedClass(isExpanded ? null : cls.id)} 
                                            style={{ 
                                                padding: '18px 24px', 
                                                display: 'flex', 
                                                justifyContent: 'space-between', 
                                                alignItems: 'center', 
                                                cursor: 'pointer',
                                                background: isExpanded ? 'var(--btn-hover)' : 'transparent',
                                                userSelect: 'none'
                                            }}
                                        >
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                                <div style={{ fontSize: '16px', color: 'var(--text-primary)', fontWeight: '700' }}>{cls.topic}</div>
                                                <div style={{ fontSize: '12px', color: 'var(--text-secondary)', fontWeight: '600' }}>
                                                    📅 {new Date(cls.endedAt || cls.scheduledAt).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' })}
                                                </div>
                                            </div>

                                            <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                                                <div style={{ background: 'var(--bg-primary)', padding: '5px 12px', borderRadius: '8px', color: '#10b981', fontWeight: 'bold', fontSize: '11px', border: '1px solid rgba(255,255,255,0.6)', boxShadow: '2px 2px 5px rgba(163,177,198,0.25), -2px -2px 5px rgba(255,255,255,0.7)' }}>
                                                    👥 {attendanceCount} / {cls.students?.length || 0} Joined
                                                </div>
                                                <span style={{ color: 'var(--text-secondary)', fontSize: '13px', transform: isExpanded ? 'rotate(180deg)' : 'none', transition: '0.3s' }}>▼</span>
                                            </div>
                                        </div>
                                        
                                        {/* Expanded Report Details */}
                                        {isExpanded && (
                                            <div style={{ padding: '24px 28px', background: '#e0e5ec', borderTop: '1px solid rgba(255,255,255,0.45)', boxShadow: 'inset 3px 3px 8px rgba(163,177,198,0.2), inset -3px -3px 8px rgba(255,255,255,0.75)', display: 'flex', flexDirection: 'column', gap: '25px' }}>
                                                
                                                {/* Attendance */}
                                                <div>
                                                    <h4 style={{ color: '#3b82f6', margin: '0 0 14px 0', fontSize: '13px', fontWeight: '800', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                                        📋 Attendance Report & Remarks
                                                    </h4>
                                                    {renderAttendance(cls)}
                                                </div>

                                                {cls.coachClassPrivateNotes && (
                                                    <>
                                                        <div style={{ height: '1px', background: 'rgba(255,255,255,0.45)' }}></div>
                                                        {/* Private Diary Log */}
                                                        <div style={{ background: '#e0e5ec', border: '1px solid rgba(255, 255, 255, 0.45)', padding: '16px', borderRadius: '12px', boxShadow: 'inset 2px 2px 5px rgba(163,177,198,0.25), inset -2px -2px 5px rgba(255,255,255,0.7)' }}>
                                                            <div style={{ color: '#ec4899', fontSize: '12px', fontWeight: '800', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                                                📓 Class Private Diary (Coaching Notes)
                                                            </div>
                                                            <div style={{ color: 'var(--text-primary)', fontSize: '13.5px', lineHeight: '1.6', whiteSpace: 'pre-line' }}>{cls.coachClassPrivateNotes}</div>
                                                        </div>
                                                    </>
                                                )}

                                                {cls.coachStickyNote && (
                                                    <>
                                                        <div style={{ height: '1px', background: 'rgba(255,255,255,0.45)' }}></div>
                                                        {/* Coach Notes */}
                                                        <div style={{ background: '#e0e5ec', border: '1px solid rgba(255, 255, 255, 0.45)', padding: '16px', borderRadius: '12px', boxShadow: 'inset 2px 2px 5px rgba(163,177,198,0.25), inset -2px -2px 5px rgba(255,255,255,0.7)' }}>
                                                            <div style={{ color: '#f59e0b', fontSize: '12px', fontWeight: '800', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                                                📝 Class Sticky Note (End Summary)
                                                            </div>
                                                            <div style={{ color: 'var(--text-primary)', fontSize: '13.5px', lineHeight: '1.6', whiteSpace: 'pre-line' }}>{cls.coachStickyNote}</div>
                                                        </div>
                                                    </>
                                                )}

                                                <div style={{ height: '1px', background: 'rgba(255,255,255,0.45)' }}></div>

                                                {/* Activity Timeline */}
                                                <div>
                                                    <h4 style={{ color: '#f59e0b', margin: '0 0 14px 0', fontSize: '13px', fontWeight: '800', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                                        ⏳ Class Activity Timeline
                                                    </h4>
                                                    {renderTimeline(cls, i)}
                                                </div>

                                            </div>
                                        )}
                                    </div>
                                );
                            })
                        )
                    )}

                    {activeTab === 'matches' && (
                        filteredMatches.length === 0 ? (
                            <div style={{ color: 'var(--text-secondary)', textAlign: 'center', padding: '40px', background: '#e0e5ec', borderRadius: '12px', boxShadow: 'var(--neu-shadow-inset)', border: '1px solid rgba(255,255,255,0.45)' }}>
                                📭 No matches matches your criteria.
                            </div>
                        ) : (
                            filteredMatches.map((g, i) => {
                                const whiteName = g.white.startsWith('bot_') ? (BOTS.find(b=>b.id===g.white)?.name || 'Bot') : (usersMap[g.white] || g.white);
                                const blackName = g.black.startsWith('bot_') ? (BOTS.find(b=>b.id===g.black)?.name || 'Bot') : (usersMap[g.black] || g.black);
                                const resultText = g.winner === 'draw' ? '🤝 ½ - ½ Draw' : (g.winner === 'w' ? `🏆 1 - 0 White Won` : g.winner === 'b' ? `🏆 0 - 1 Black Won` : '⏳ In Progress');
                                const resultColor = g.winner === 'draw' ? '#f59e0b' : (g.winner === 'w' ? '#10b981' : g.winner === 'b' ? '#f43f5e' : '#3b82f6');
                                const matchKey = `match-${g.id || i}`;
                                const isMatchExpanded = !!expandedMatches[matchKey];

                                return (
                                    <div key={g.id || i} style={{ 
                                        background: '#e0e5ec', 
                                        borderRadius: '16px', 
                                        border: '1px solid rgba(255,255,255,0.55)', 
                                        padding: '16px 20px', 
                                        display: 'flex', 
                                        flexDirection: 'column', 
                                        gap: '12px',
                                        boxShadow: isMatchExpanded ? 'var(--panel-shadow-hover)' : 'var(--panel-shadow)',
                                        cursor: 'pointer',
                                        transition: 'all 0.2s'
                                    }}
                                    onClick={() => toggleMatch(matchKey)}
                                    >
                                        {/* Match Header Row */}
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '15px' }}>
                                            <div>
                                                <div style={{ fontSize: '15px', color: 'var(--text-primary)', fontWeight: 'bold', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                    <span>⚪ {whiteName}</span> 
                                                    <span style={{ color: 'var(--text-secondary)', fontSize: '11px' }}>vs</span> 
                                                    <span>⚫ {blackName}</span>
                                                </div>
                                                <div style={{ fontSize: '12.5px', color: resultColor, fontWeight: '750' }}>{resultText}</div>
                                            </div>
                                            
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                <span style={{ fontSize: '12px', color: 'var(--text-secondary)', fontWeight: '600' }}>
                                                    📅 {new Date(g.date).toLocaleDateString([], { dateStyle: 'medium' })}
                                                </span>
                                                <span style={{ color: 'var(--text-secondary)', fontSize: '13px', transform: isMatchExpanded ? 'rotate(180deg)' : 'none', transition: '0.3s' }}>▼</span>
                                            </div>
                                        </div>

                                        {/* Collapsible Details */}
                                        {isMatchExpanded && (
                                            <div 
                                                style={{ 
                                                    borderTop: '1px solid rgba(163, 177, 198, 0.3)', 
                                                    paddingTop: '15px', 
                                                    marginTop: '5px',
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    gap: '12px',
                                                    animation: 'fadeIn 0.25s ease-out' 
                                                }}
                                                onClick={(e) => e.stopPropagation()} // Prevent collapsing when clicking inner contents
                                            >
                                                <div style={{ fontSize: '12.5px', color: 'var(--text-secondary)', fontWeight: '600' }}>
                                                    Class Context: <span style={{ color: 'var(--text-primary)' }}>{g.className}</span>
                                                </div>

                                                <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                                                    <button 
                                                        onClick={(e) => toggleBoard(matchKey, e)}
                                                        style={{ padding: '8px 14px', background: 'var(--bg-primary)', border: '1px solid rgba(255,255,255,0.6)', color: '#3b82f6', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer', fontSize: '12px', boxShadow: '1.5px 1.5px 4px rgba(163,177,198,0.2), -1.5px -1.5px 4px rgba(255,255,255,0.85)', transition: 'all 0.2s' }}
                                                    >
                                                        👁️ {visibleBoards[matchKey] ? 'Hide Board' : 'View Position'}
                                                    </button>
                                                    <button 
                                                        onClick={() => { navigator.clipboard.writeText(g.pgn || ''); toast.success("PGN Copied!"); }} 
                                                        style={{ padding: '8px 14px', background: 'var(--bg-primary)', border: '1px solid rgba(255,255,255,0.6)', color: 'var(--text-primary)', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer', fontSize: '12px', boxShadow: '1.5px 1.5px 4px rgba(163,177,198,0.2), -1.5px -1.5px 4px rgba(255,255,255,0.85)', transition: 'all 0.2s' }}
                                                    >
                                                        📋 Copy PGN
                                                    </button>
                                                    <button 
                                                        onClick={() => { navigator.clipboard.writeText(g.fen || ''); toast.success("FEN Copied!"); }} 
                                                        style={{ padding: '8px 14px', background: 'var(--bg-primary)', border: '1px solid rgba(255,255,255,0.6)', color: 'var(--text-primary)', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer', fontSize: '12px', boxShadow: '1.5px 1.5px 4px rgba(163,177,198,0.2), -1.5px -1.5px 4px rgba(255,255,255,0.85)', transition: 'all 0.2s' }}
                                                    >
                                                        📋 Copy FEN
                                                    </button>
                                                </div>

                                                {visibleBoards[matchKey] && g.fen && (
                                                    <div style={{ borderTop: '1px dashed rgba(163, 177, 198, 0.4)', paddingTop: '10px', animation: 'fadeIn 0.2s ease-out' }}>
                                                        <MiniPreviewBoard fen={g.fen} />
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                );
                            })
                        )
                    )}

                </div>
            </div>
        </div>
    );
}
